import React, { useState, useEffect, useCallback } from 'react';
import { 
  Monitor, RefreshCw, Save, ToggleLeft, ToggleRight, Percent, DollarSign, 
  AlertCircle, CheckCircle, Loader2, Package, Eye, EyeOff, RotateCcw, CloudDownload,
  FileDown, ClipboardList, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Display configurations - Updated to match backend
const DISPLAYS = [
  { id: 1, name: 'Window Display #1', categories: ['pastries', 'pies'], description: 'Pastries & Pies' },
  { id: 2, name: 'Window Display #2', categories: ['pastries', 'pies'], description: 'Pastries & Pies (offset)' },
  { id: 3, name: 'Window Display #3', categories: ['grab-and-go', 'breakfast', 'lunch'], description: 'Grab & Go, Breakfast & Lunch' }
];

const CATEGORY_NAMES = {
  'muffins': 'Muffins',
  'pastries': 'Pastries',
  'pies': 'Pies',
  'sweets': 'Sweets',
  'grab-and-go': 'Grab & Go',
  'breakfast': 'Breakfast',
  'lunch': 'Lunch'
};

// Categories that don't show quantity (unlimited items)
const NO_QUANTITY_CATEGORIES = ['breakfast', 'lunch'];

const DisplayManagementTab = () => {
  const [selectedDisplay, setSelectedDisplay] = useState(1);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fijiTime, setFijiTime] = useState('');
  const [editedItems, setEditedItems] = useState({});

  // Fetch inventory for selected display
  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/display/inventory/${selectedDisplay}`);
      setInventory(response.data.items || []);
      setFijiTime(response.data.fiji_time || '');
      setEditedItems({});
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [selectedDisplay]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // Sync menu from production
  const syncFromProduction = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/display/sync-from-production');
      const data = response.data;
      toast.success(`Menu synced! ${data.items_fetched} items loaded from production menu.`);
      // Refresh the inventory after sync
      fetchInventory();
    } catch (error) {
      console.error('Failed to sync:', error);
      toast.error('Failed to sync from production menu');
    } finally {
      setSyncing(false);
    }
  };

  // Update local state when editing
  const handleInputChange = (itemId, field, value) => {
    setEditedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  // Get current value (edited or original)
  const getValue = (item, field) => {
    if (editedItems[item.item_id] && editedItems[item.item_id][field] !== undefined) {
      return editedItems[item.item_id][field];
    }
    return item[field];
  };

  // Save single item - syncs across all displays
  const saveItem = async (item) => {
    setSaving(true);
    try {
      const updates = editedItems[item.item_id] || {};
      
      // Parse current quantity carefully to avoid NaN
      const parsedCurrent = updates.current_quantity !== undefined 
        ? parseInt(updates.current_quantity, 10) 
        : item.current_quantity;
      
      const payload = {
        item_id: item.item_id,
        current_quantity: isNaN(parsedCurrent) ? 0 : parsedCurrent,
        is_active: updates.is_active !== undefined ? updates.is_active : (item.is_active ?? true),
        markdown_price: updates.markdown_price !== undefined && updates.markdown_price !== '' 
          ? parseFloat(updates.markdown_price) 
          : null,
        markdown_percent: updates.markdown_percent !== undefined && updates.markdown_percent !== '' 
          ? parseInt(updates.markdown_percent, 10) 
          : null
      };

      console.log('Saving item:', item.name, 'with payload:', payload);
      
      const response = await api.post('/display/inventory/update', payload);
      
      console.log('Save response:', response.data);
      
      if (response.data.status === 'ok') {
        const syncMsg = response.data.synced 
          ? ` (synced to Displays ${response.data.displays?.join(' & ') || '1 & 2'})` 
          : '';
        toast.success(`Saved ${item.name}: Qty=${payload.current_quantity}${syncMsg}`);
        // Wait a moment before refetching to ensure DB write completes
        setTimeout(() => {
          fetchInventory();
        }, 300);
      } else {
        toast.error('Save returned unexpected status');
      }
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error(error.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Toggle item active status
  const toggleActive = async (item) => {
    try {
      await api.post(`/display/inventory/toggle/${item.item_id}`);
      toast.success(`${item.name} ${item.is_active ? 'hidden' : 'shown'} on display`);
      fetchInventory();
    } catch (error) {
      console.error('Failed to toggle:', error);
      toast.error('Failed to toggle item');
    }
  };

  // Clear markdown for item
  const clearMarkdown = async (item) => {
    try {
      await api.post(`/display/inventory/clear-markdown/${item.item_id}`);
      toast.success(`Cleared markdown for ${item.name}`);
      fetchInventory();
    } catch (error) {
      console.error('Failed to clear markdown:', error);
      toast.error('Failed to clear markdown');
    }
  };

  // Reset all inventory for display
  const resetAllInventory = async () => {
    const resetQty = window.prompt('Enter the quantity to reset all items to:', '10');
    if (resetQty === null) return;
    
    const qty = parseInt(resetQty, 10);
    if (isNaN(qty) || qty < 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    setSaving(true);
    try {
      const items = inventory.map(item => ({
        item_id: item.item_id,
        quantity: qty
      }));

      await api.post(`/display/inventory/reset/${selectedDisplay}`, { 
        display_number: selectedDisplay, 
        items 
      });

      toast.success(`All inventory reset to ${qty}!`);
      fetchInventory();
    } catch (error) {
      console.error('Failed to reset:', error);
      toast.error('Failed to reset inventory');
    } finally {
      setSaving(false);
    }
  };

  // Add global rank to items before grouping (items are already sorted by sales)
  const itemsWithRank = inventory.map((item, index) => ({
    ...item,
    globalRank: index + 1  // Preserve the global rank from API's sorted order
  }));

  // Group items by category while preserving globalRank
  const groupedItems = itemsWithRank.reduce((acc, item) => {
    const cat = item.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Monitor className="w-6 h-6 text-amber-600" />
            Window Display Management
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage fresh baked goods inventory for window displays
          </p>
          {fijiTime && (
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              Fiji Time: {fijiTime}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            onClick={syncFromProduction} 
            disabled={syncing}
            className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            data-testid="sync-from-production-btn"
          >
            <CloudDownload className={`w-4 h-4 mr-2 ${syncing ? 'animate-bounce' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from Live Menu'}
          </Button>
          <Button variant="outline" onClick={fetchInventory} disabled={loading} data-testid="refresh-inventory-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={resetAllInventory} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="reset-inventory-btn">
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset Day
          </Button>
        </div>
      </div>

      {/* Sync Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <CloudDownload className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-blue-800 dark:text-blue-200 font-medium">Sync from Live Menu</p>
            <p className="text-sm text-blue-600 dark:text-blue-300 mt-1">
              Click the blue button above to pull the latest menu items from your production website. 
              This updates all display items with current images, prices, and names.
            </p>
          </div>
        </div>
      </div>

      {/* Cross-Display Sync Notice */}
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <RefreshCw className="w-5 h-5 text-green-600 mt-0.5" />
          <div>
            <p className="text-green-800 dark:text-green-200 font-medium">Automatic Cross-Display Sync</p>
            <p className="text-sm text-green-600 dark:text-green-300 mt-1">
              When you update an item&apos;s quantity here, it automatically syncs to ALL displays where that item appears. 
              No need to update Display 1 and Display 2 separately!
            </p>
          </div>
        </div>
      </div>

      {/* Inventory Count Sheets */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <ClipboardList className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-amber-800 dark:text-amber-200 font-medium">Printable Inventory Count Sheets</p>
            <p className="text-sm text-amber-600 dark:text-amber-300 mt-1 mb-3">
              Download and print these forms for staff to manually count inventory at opening and closing.
            </p>
            <div className="flex flex-wrap gap-2">
              <a 
                href="/api/display/inventory/count-sheet/opening"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="download-opening-sheet"
              >
                <FileDown className="w-4 h-4" />
                Opening Inventory (PDF)
              </a>
              <a 
                href="/api/display/inventory/count-sheet/closing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="download-closing-sheet"
              >
                <FileDown className="w-4 h-4" />
                Closing Inventory (PDF)
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Display Selector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {DISPLAYS.map(display => (
          <button
            key={display.id}
            onClick={() => setSelectedDisplay(display.id)}
            data-testid={`display-selector-${display.id}`}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              selectedDisplay === display.id
                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-amber-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                selectedDisplay === display.id ? 'bg-amber-500 text-white' : 'bg-gray-100 dark:bg-gray-800'
              }`}>
                <Monitor className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{display.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{display.description}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Preview Link */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600" />
            <span className="text-blue-800 dark:text-blue-200 font-medium">Preview Display</span>
          </div>
          <a 
            href={`/display${selectedDisplay}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline font-mono text-sm"
          >
            /display{selectedDisplay} →
          </a>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading inventory...</span>
        </div>
      ) : inventory.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">No items configured</h3>
          <p className="text-gray-500 dark:text-gray-400">Items from the selected categories will appear here.</p>
        </div>
      ) : (
        /* Inventory Items by Category */
        Object.entries(groupedItems).map(([category, items]) => (
          <div key={category} className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2 dark:border-gray-700">
              {CATEGORY_NAMES[category] || category}
            </h3>
            
            <div className="grid gap-4">
              {items.map((item) => (
                <div 
                  key={item.item_id}
                  className={`p-4 rounded-xl border ${
                    item.is_active 
                      ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' 
                      : 'bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 opacity-60'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row gap-4">
                    {/* Item Image & Info */}
                    <div className="flex items-center gap-4 lg:w-1/3">
                      {/* Global rank indicator based on sales (preserved from API sort) */}
                      <div className="flex flex-col items-center gap-1" data-testid={`item-rank-${item.item_id}`}>
                        <span className="text-xs text-gray-400 dark:text-gray-500">#{item.globalRank}</span>
                        <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400" data-testid={`total-sold-${item.item_id}`}>
                          <TrendingUp className="w-3 h-3" />
                          {item.total_sold || 0}
                        </div>
                      </div>
                      <img 
                        src={item.image_url} 
                        alt={item.name}
                        className="w-16 h-16 rounded-lg object-cover"
                        onError={(e) => { e.target.src = '/placeholder-food.jpg'; }}
                      />
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{item.name}</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Regular: ${item.original_price.toFixed(2)}
                        </p>
                        {item.is_marked_down && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded-full">
                            Sale: ${item.effective_price.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Inventory Controls - Simplified to just Qty Available */}
                    <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {/* Qty Available (current_quantity) */}
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Qty Available</label>
                        <Input
                          type="number"
                          min="0"
                          value={getValue(item, 'current_quantity')}
                          onChange={(e) => handleInputChange(item.item_id, 'current_quantity', e.target.value)}
                          className={`text-center ${
                            item.current_quantity <= 0 ? 'border-red-500 bg-red-50 dark:bg-red-900/20' :
                            item.current_quantity <= 3 ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : ''
                          }`}
                          data-testid={`qty-input-${item.item_id}`}
                        />
                      </div>

                      {/* Markdown % */}
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          <Percent className="w-3 h-3 inline mr-1" />
                          Discount %
                        </label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          placeholder="0"
                          value={getValue(item, 'markdown_percent') || ''}
                          onChange={(e) => handleInputChange(item.item_id, 'markdown_percent', e.target.value)}
                          data-testid={`discount-input-${item.item_id}`}
                        />
                      </div>

                      {/* Markdown Price */}
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          <DollarSign className="w-3 h-3 inline mr-1" />
                          Sale Price
                        </label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={item.original_price.toFixed(2)}
                          value={getValue(item, 'markdown_price') || ''}
                          onChange={(e) => handleInputChange(item.item_id, 'markdown_price', e.target.value)}
                          data-testid={`sale-price-input-${item.item_id}`}
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 lg:w-auto">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActive(item)}
                        className={item.is_active ? 'text-green-600' : 'text-gray-400'}
                        title={item.is_active ? 'Hide from display' : 'Show on display'}
                        data-testid={`toggle-active-${item.item_id}`}
                      >
                        {item.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                      </Button>
                      
                      {item.is_marked_down && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => clearMarkdown(item)}
                          className="text-red-500"
                          title="Clear markdown"
                          data-testid={`clear-markdown-${item.item_id}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        onClick={() => saveItem(item)}
                        disabled={saving || !editedItems[item.item_id]}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid={`save-btn-${item.item_id}`}
                      >
                        <Save className="w-4 h-4 mr-1" />
                        Save
                      </Button>
                    </div>
                  </div>

                  {/* Status Indicators */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {item.current_quantity <= 0 && (
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded-full flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> SOLD OUT
                      </span>
                    )}
                    {item.current_quantity > 0 && item.current_quantity <= 3 && (
                      <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs rounded-full flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Low Stock
                      </span>
                    )}
                    {item.is_active && (
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Showing on display
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Help Text */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 mt-6">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">How It Works</h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <li>• <strong>Sorted by Sales:</strong> Items are listed from best-selling (top) to least-selling (bottom)</li>
          <li>• <strong>Qty Available:</strong> Enter the amount you have available - it counts down when items sell via POS</li>
          <li>• <strong>Synced Across Displays:</strong> Updating an item here updates it on ALL displays where it appears</li>
          <li>• <strong>Discount %:</strong> Set a percentage discount (e.g., 50 = 50% off)</li>
          <li>• <strong>Sale Price:</strong> Or set a specific sale price directly</li>
          <li>• <strong>Eye icon:</strong> Toggle to show/hide item from display</li>
          <li>• <strong>Reset Day:</strong> Sets all items to a quantity you specify</li>
          <li>• Markdowns auto-reset at 5:00 PM Fiji time</li>
        </ul>
      </div>
    </div>
  );
};

export default DisplayManagementTab;
